import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pause, Play, Trash2, Gift, Users2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  signup_bonus: "Signup bonus",
  referral_bonus: "Referral bonus",
  first_purchase_bonus: "First-purchase bonus",
  category_bonus: "Category bonus",
};

const blank = {
  name: "",
  description: "",
  type: "signup_bonus",
  bonus_amount: 10,
  target_audience: "all_new_users",
  target_category: "",
  start_date: "",
  end_date: "",
  max_recipients: 1000,
  status: "active",
};

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get("/campaigns");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.name) {
      toast.error("Name required");
      return;
    }
    try {
      const payload = {
        ...form,
        bonus_amount: parseFloat(form.bonus_amount),
        max_recipients: parseInt(form.max_recipients),
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
        target_category: form.target_category || null,
      };
      await api.post("/campaigns", payload);
      toast.success("Campaign created");
      setForm(blank);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create");
    }
  };

  const togglePause = async (c) => {
    const next = c.status === "active" ? "paused" : "active";
    await api.put(`/campaigns/${c.id}`, { status: next });
    toast.success(`Campaign ${next}`);
    load();
  };

  const end = async (id) => {
    if (!window.confirm("End this campaign? Past claims remain valid.")) return;
    await api.delete(`/campaigns/${id}`);
    load();
  };

  const totalPaid = items.reduce((s, c) => s + (c.total_paid || 0), 0);
  const totalClaims = items.reduce((s, c) => s + (c.claimed_count || 0), 0);

  return (
    <div data-testid="adm-campaigns-page">
      <PageHeader
        eyebrow="Growth"
        title="Bonus campaigns"
        subtitle="Acquire and reactivate users with targeted cashback bonuses."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="new-campaign">
                <Plus className="w-4 h-4 mr-2" /> New campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create bonus campaign</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="mb-1.5 block">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Holiday $10 bonus" data-testid="camp-name" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Limited-time $10 cashback for new signups." />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1.5 block">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="signup_bonus">Signup bonus</SelectItem>
                        <SelectItem value="referral_bonus">Referral bonus</SelectItem>
                        <SelectItem value="first_purchase_bonus">First-purchase bonus</SelectItem>
                        <SelectItem value="category_bonus">Category bonus</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Bonus amount ($)</Label>
                    <Input type="number" min="0.01" step="0.5" value={form.bonus_amount} onChange={(e) => setForm({ ...form, bonus_amount: e.target.value })} data-testid="camp-amount" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Audience</Label>
                    <Select value={form.target_audience} onValueChange={(v) => setForm({ ...form, target_audience: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_new_users">All new users</SelectItem>
                        <SelectItem value="referred_users">Referred users only</SelectItem>
                        <SelectItem value="category_shoppers">Category shoppers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Max recipients</Label>
                    <Input type="number" min="-1" value={form.max_recipients} onChange={(e) => setForm({ ...form, max_recipients: e.target.value })} placeholder="-1 for unlimited" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Start date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">End date</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                  </div>
                </div>
                {form.target_audience === "category_shoppers" && (
                  <div>
                    <Label className="mb-1.5 block">Target category</Label>
                    <Input value={form.target_category} onChange={(e) => setForm({ ...form, target_category: e.target.value })} placeholder="groceries / travel / dining…" />
                  </div>
                )}
                <div className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <strong>Cost projection:</strong> {fmtMoney(parseFloat(form.bonus_amount || 0) * parseInt(form.max_recipients || 0))} max payout if all recipients claim.
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="create-campaign">
                  Launch campaign
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold">Active campaigns</div>
            <div className="font-display font-bold text-2xl mt-1">{items.filter((c) => c.status === "active").length}</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold">Total claims</div>
            <div className="font-display font-bold text-2xl mt-1">{totalClaims}</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold">Total paid out</div>
            <div className="font-display font-bold text-2xl mt-1 text-emerald-700">{fmtMoney(totalPaid)}</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold">Avg cost per acquisition</div>
            <div className="font-display font-bold text-2xl mt-1">{totalClaims > 0 ? fmtMoney(totalPaid / totalClaims) : "—"}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-gray-200 shadow-none rounded-2xl">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 font-semibold">Campaign</th>
                <th className="px-5 py-3 font-semibold">Type</th>
                <th className="px-5 py-3 font-semibold">Bonus</th>
                <th className="px-5 py-3 font-semibold">Claims</th>
                <th className="px-5 py-3 font-semibold">Paid out</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const pct = c.max_recipients > 0 ? Math.round((c.claimed_count / c.max_recipients) * 100) : 0;
                return (
                  <tr key={c.id} className="border-b border-gray-100" data-testid={`camp-${c.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium">{c.name}</div>
                      {c.description && <div className="text-xs text-gray-500">{c.description}</div>}
                    </td>
                    <td className="px-5 py-3"><Badge variant="outline">{TYPE_LABELS[c.type] || c.type}</Badge></td>
                    <td className="px-5 py-3 font-semibold">{fmtMoney(c.bonus_amount)}</td>
                    <td className="px-5 py-3">
                      <div className="font-semibold">{c.claimed_count} / {c.max_recipients < 0 ? "∞" : c.max_recipients}</div>
                      {c.max_recipients > 0 && (
                        <div className="w-24 h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                          <div className="h-full bg-emerald-700" style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-emerald-700 font-semibold">{fmtMoney(c.total_paid)}</td>
                    <td className="px-5 py-3">
                      <Badge className={
                        c.status === "active" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" :
                        c.status === "paused" ? "bg-amber-50 text-amber-800 border border-amber-200" :
                        "bg-gray-50 text-gray-700 border border-gray-200"
                      }>{c.status}</Badge>
                    </td>
                    <td className="px-5 py-3 flex gap-1">
                      {c.status !== "ended" && (
                        <button
                          onClick={() => togglePause(c)}
                          className="p-1.5 text-gray-500 hover:text-emerald-700"
                          title={c.status === "active" ? "Pause" : "Resume"}
                          data-testid={`toggle-${c.id}`}
                        >
                          {c.status === "active" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                      )}
                      <button onClick={() => end(c.id)} className="p-1.5 text-gray-500 hover:text-red-600" title="End">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan="7" className="px-5 py-16 text-center">
                  <Gift className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <div className="text-gray-500">No campaigns yet. Launch your first signup bonus to start acquiring users.</div>
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
